import { InputError } from '@foodsby/nutrient'
import { Box, Button, Chip, Grid, Typography } from '@material-ui/core'
import { CheckCircle } from '@material-ui/icons'
import { Alert } from '@material-ui/lab'
import React, { useEffect, useState } from 'react'
import { Field, reduxForm } from 'redux-form'
import { confirmation, email, length, required } from 'redux-form-validators'

import { normalizeProfileImageErrors } from '../../util/errorUtils'
import { formatTenDigitPhoneNumber } from '../../util/formatUtils'
import { noSpacesAllowed } from '../../util/validators'
import { Segment } from '../common/Layout'
import ModalPopup from '../common/ModalPopup'
import Input from '../common/reduxForms/Input'
import { Submitting } from '../common/Submitting'
import PanelHeader from './PanelHeader'
import DialogChangeEmail from './personal-information/DialogChangeEmail'
import EditProfileButtons from './personal-information/EditProfileButtons'
import { ProfileImage } from './ProfilePicture'

export const PersonalInformationPanel = ({
  changePasswordEditingMode,
  changePasswordFailed,
  changePasswordStart,
  changePersonalInfoEditingMode,
  isChangingPassword,
  isEditingPersonalInfo,
  isWebpSupported,
  profile: { avatarId, firstName, lastName, phone, username, verifiedEmail },
  submitting,
  success,
  updatePersonalInfoError,
  updatePersonalInfoStart,
  updateProfilePictureError,
  updateProfilePictureLoading,
  updateProfilePictureStart,
}) => {
  const [showEmailChangeDialog, setShowEmailChangeDialog] = useState(false)
  const [submitValues, setSubmitValues] = useState(null)

  const handleSubmit = values => {
    if (values.email !== username) {
      // Show the dialog for confirmation
      setShowEmailChangeDialog(true)
      // Set the submit values as a state just so we can access them upon the password confirmation in DialogChangeEmail
      setSubmitValues(values)
    } else {
      updatePersonalInfoStart(values)
    }
  }

  const handleSubmitAndEmailChange = values => {
    updatePersonalInfoStart(submitValues, values.password)
  }

  useEffect(() => {
    if (!updatePersonalInfoError && !isEditingPersonalInfo) {
      setShowEmailChangeDialog(false)
    }
  }, [updatePersonalInfoError, isEditingPersonalInfo])

  return (
    <Segment>
      <PanelHeader
        headerRight={
          <EditProfileButtons
            changePersonalInfoEditingMode={changePersonalInfoEditingMode}
            formName={'personalInfo'}
            isEditing={isEditingPersonalInfo}
            submitting={submitting}
          />
        }
        title="Personal Information"
      />

      <div className="row">
        <div className="profile-image-container col-sm-5 center-xs col-xs-12">
          <ProfileImage
            avatarId={avatarId}
            isWebpSupported={isWebpSupported}
            name={firstName && lastName && `${firstName} ${lastName}`}
            updateProfilePictureLoading={updateProfilePictureLoading}
            updateProfilePictureStart={updateProfilePictureStart}
          />
          {updateProfilePictureError && (
            <InputError>
              {normalizeProfileImageErrors(updateProfilePictureError.message || '')
                .slice(0, 1)
                .map((error, i) => (
                  <div key={i}>{error}</div>
                ))}
            </InputError>
          )}
        </div>
        <div className="col-xs-12 col-sm-7">
          <Box marginBottom={3}>
            {isEditingPersonalInfo ? (
              <ReduxEditPersonalInformationForm
                changePersonalInfoEditingMode={changePersonalInfoEditingMode}
                initialValues={{
                  email: username,
                  firstName,
                  lastName,
                  phone,
                }}
                onSubmit={handleSubmit}
                submitting={submitting}
              />
            ) : (
              <ProfileNamePanel
                changePersonalInfoEditingMode={changePersonalInfoEditingMode}
                email={username}
                firstName={firstName}
                lastName={lastName}
                phone={phone}
                verifiedEmail={verifiedEmail}
              />
            )}
          </Box>
          <ChangePassword
            changePasswordEditingMode={changePasswordEditingMode}
            changePasswordStart={changePasswordStart}
            email={username}
            failure={changePasswordFailed}
            isChangingPassword={isChangingPassword}
            submitting={submitting}
            success={success}
          />
        </div>
      </div>
      <DialogChangeEmail
        onClose={() => setShowEmailChangeDialog(false)}
        onSubmit={handleSubmitAndEmailChange}
        submitting={submitting}
        updatePersonalInfoError={updatePersonalInfoError}
        visible={showEmailChangeDialog}
      />
    </Segment>
  )
}

PersonalInformationPanel.defaultProps = {
  profile: {},
}

export const ProfileNamePanel = ({ email, firstName, lastName, phone, verifiedEmail }) => (
  <Grid container spacing={2}>
    <Grid item xs={6}>
      <Typography variant="caption">First Name</Typography>
      <Typography variant="body1">{firstName ? firstName : 'No First Name Provided'}</Typography>
    </Grid>
    <Grid item xs={6}>
      <Typography variant="caption">Last Name</Typography>
      <Typography variant="body1">{lastName ? lastName : 'No Last Name Provided'}</Typography>
    </Grid>
    <Grid item xs={12}>
      <Typography variant="caption">Phone Number</Typography>
      <Typography variant="body1">
        {phone ? formatTenDigitPhoneNumber(phone) : 'No Phone Number Provided'}
      </Typography>
    </Grid>
    <Grid item xs={12}>
      <Typography variant="caption">Email</Typography>
      <Box display="flex" flexDirection="row">
        <Typography variant="body1">{email ? email : 'No Email Provided'}</Typography>&nbsp;&nbsp;
        {verifiedEmail && (
          <Chip color="secondary" icon={<CheckCircle />} label="Verified" size="small" />
        )}
      </Box>
    </Grid>
  </Grid>
)

export const EditPersonalInformationForm = ({ handleSubmit }) => (
  <form onSubmit={handleSubmit}>
    <div className="row">
      <div className="col-xs-6">
        <div className="Input">
          <Field
            className="ProCard-inputLabel"
            component={Input}
            id="firstName"
            label="First Name"
            maxLength="35"
            name="firstName"
            type="text"
            validate={[noSpacesAllowed, required({ msg: `First Name is required` })]}
          />
        </div>
      </div>
      <div className="col-xs-6">
        <div className="Input">
          <Field
            className="ProCard-inputLabel"
            component={Input}
            id="lastName"
            label="Last Name"
            maxLength="35"
            name="lastName"
            type="text"
            validate={[noSpacesAllowed, required({ msg: `Last Name is required` })]}
          />
        </div>
      </div>
    </div>
    <div className="Input">
      <Field
        className="ProCard-inputLabel"
        component={Input}
        id="phone"
        label="Phone Number"
        name="phone"
        normalize={val => val.replace(/\D/g, '')}
        props={{
          format: '(###) ###-####',
          mask: '_',
        }}
        type="tel"
        validate={[
          noSpacesAllowed,
          required({ msg: `Phone Number is required` }),
          length({
            is: 10,
            msg: { wrongLength: `Phone Number must be 10 digits` },
          }),
        ]}
      />
    </div>
    <div className="Input">
      <Field
        autoComplete="off"
        className="ProCard-inputLabel"
        component={Input}
        id="email"
        label="Email"
        name="email"
        type="email"
        validate={[noSpacesAllowed, required({ msg: `Email is required` }), email()]}
      />
    </div>
  </form>
)

export const ReduxEditPersonalInformationForm = reduxForm({
  form: 'personalInfo',
})(EditPersonalInformationForm)

export const ChangePasswordModal = ({
  changePasswordEditingMode,
  failure,
  handleSubmit,
  invalid,
  pristine,
  submitting,
}) => (
  <form onSubmit={handleSubmit}>
    <div className="Input">
      <Field
        component={Input}
        id="oldPassword"
        label="Current Password"
        name="oldPassword"
        type="password"
        validate={[
          required({ msg: `Current Password is required` }),
          length({
            minimum: 6,
            msg: 'Current Password is too short',
          }),
        ]}
      />
    </div>
    <div className="Input">
      <Field
        component={Input}
        id="password"
        label="New Password"
        name="password"
        type="password"
        validate={[
          noSpacesAllowed,
          required({ msg: `New Password is required` }),
          length({ minimum: 8, msg: 'New Password is too short' }),
        ]}
      />
    </div>
    <div className="Input">
      <Field
        component={Input}
        id="confirmPassword"
        label="Confirm Password"
        name="confirmPassword"
        type="password"
        validate={[
          noSpacesAllowed,
          confirmation({
            field: 'password',
            fieldLabel: 'New Password',
            msg: 'Password does not match New Password',
          }),
        ]}
      />
    </div>
    <div className="row middle-xs">
      <div className="col-xs center-xs">
        <Button onClick={() => changePasswordEditingMode(false)}>Cancel</Button>
      </div>
      <div className="col-xs center-xs">
        {submitting ? (
          <Submitting />
        ) : (
          <Button color="primary" disabled={invalid || pristine} type="submit" variant="contained">
            Save Changes
          </Button>
        )}
      </div>
    </div>
    {failure && (
      <Box marginTop={3}>
        <Alert severity="error">
          Sorry your password was not changed, please verify the information entered and try again.
        </Alert>
      </Box>
    )}
  </form>
)

export const ChangePasswordForm = reduxForm({
  form: 'changePassword',
})(ChangePasswordModal)

export const ChangePassword = ({
  changePasswordEditingMode,
  changePasswordStart,
  failure,
  isChangingPassword,
  success,
}) => (
  <>
    <Button color="primary" onClick={() => changePasswordEditingMode(true)} size="small">
      Change Password
    </Button>
    {isChangingPassword && (
      <ModalPopup showCancel={false}>
        <ChangePasswordForm
          changePasswordEditingMode={changePasswordEditingMode}
          failure={failure}
          onSubmit={changePasswordStart}
        />
      </ModalPopup>
    )}
    {success && <Alert severity="success">Your password has been successfully changed.</Alert>}
  </>
)

export default PersonalInformationPanel
